import React, { useEffect, useState } from 'react';
import { ChevronRight, Check, Sparkles, Crown, Shield, Lock, User, Bell, Trash2, AlertTriangle, Save, Mail, Settings } from 'lucide-react';
import { WebsiteHeader } from '../components/Dashboard/WebsiteHeader';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { ChangeEmailModal } from '../components/Settings/ChangeEmailModal';
import { TwoFactorModal } from '../components/Settings/TwoFactorModal';
import { PrivacySettingsModal } from '../components/Settings/PrivacySettingsModal';
import { SubscriptionModal } from '../components/Settings/SubscriptionModal';
import { DeleteAccountModal } from '../components/Settings/DeleteAccountModal';
import { BillingManagementModal } from '../components/Settings/BillingManagementModal';
import { CancelSubscriptionModal } from '../components/Settings/CancelSubscriptionModal';
import { deleteUserAccount } from '../services/accountService';
import { supabase } from '../lib/supabaseClient';
export function SettingsPage() {
  const navigate = useNavigate();
  const {
    user,
    signOut,
    updateUserProfile
  } = useAuth();
  const {
    showToast
  } = useToast();
  const [formData, setFormData] = useState({
    firstName: user?.user_metadata?.firstName || '',
    lastName: user?.user_metadata?.lastName || '',
    email: user?.email || ''
  });
  const [emailNotifications, setEmailNotifications] = useState(user?.user_metadata?.emailNotifications ?? true);
  const [pushNotifications, setPushNotifications] = useState(user?.user_metadata?.pushNotifications ?? true);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
  const [showTwoFactorModal, setShowTwoFactorModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [profile, setProfile] = useState<{
    is_pro: boolean;
    scan_credits: number;
  } | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const currentPlan = user?.user_metadata?.subscriptionPlan || 'free';
  useEffect(() => {
    async function fetchProfile() {
      if (!user?.id) return;
      try {
        const {
          data,
          error
        } = await supabase.from('profiles').select('is_pro, scan_credits').eq('id', user.id).single();
        if (error) throw error;
        setProfile(data);
      } catch (error) {
        console.error('Error fetching profile:', error);
        setProfile(null);
      } finally {
        setLoadingProfile(false);
      }
    }
    fetchProfile();
    const channel = supabase.channel('profile-settings-updates').on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'profiles',
      filter: `id=eq.${user?.id}`
    }, payload => {
      const newProfile = payload.new as {
        is_pro: boolean;
        scan_credits: number;
      };
      setProfile(newProfile);
    }).subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);
  const isPro = profile?.is_pro || false;
  const scansCount = profile?.scan_credits || 0;
  const userTier = isPro ? 'pro' : scansCount > 0 ? 'active' : 'free';
  const handleSaveChanges = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await updateUserProfile({
        firstName: formData.firstName,
        lastName: formData.lastName
      });
      setSuccess('Profile updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };
  const handleNotificationChange = async (type: 'email' | 'push', value: boolean) => {
    setError(null);
    setSuccess(null);
    if (type === 'email') {
      setEmailNotifications(value);
    } else {
      setPushNotifications(value);
    }
    try {
      await updateUserProfile({
        emailNotifications: type === 'email' ? value : emailNotifications,
        pushNotifications: type === 'push' ? value : pushNotifications
      });
      setSuccess(`${type === 'email' ? 'Email' : 'Push'} notifications ${value ? 'enabled' : 'disabled'}`);
      setTimeout(() => setSuccess(null), 2000);
    } catch (err: any) {
      if (type === 'email') {
        setEmailNotifications(!value);
      } else {
        setPushNotifications(!value);
      }
      setError(`Failed to update ${type} notification settings`);
    }
  };
  const handleLogout = async () => {
    await signOut();
    navigate('/');
    showToast('Logged out successfully', 'success');
  };
  const handleDeleteAccount = async () => {
    if (!user) return;
    setDeleteLoading(true);
    setError(null);
    try {
      await deleteUserAccount(user.id);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Failed to delete account');
      setShowDeleteAccountModal(false);
    } finally {
      setDeleteLoading(false);
    }
  };
  return <div className="flex flex-col w-full min-h-screen bg-[#050505] relative overflow-hidden" data-id="element-10018">
      {/* Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none fixed" data-id="element-10019" />

      <WebsiteHeader data-id="element-10020" />

      <div className="max-w-3xl mx-auto w-full relative z-10" data-id="element-10021">
        <main className="px-6 pt-12 pb-24" data-id="element-10022">
          {/* Page Header */}
          <div className="mb-12" data-id="element-10023">
            <div className="inline-flex items-center gap-2 mb-4 bg-[#FF0000]/10 border border-[#FF0000]/30 px-3 py-1" data-id="element-10024">
              <Settings size={14} className="text-[#FF0000]" data-id="element-10025" />
              <span className="text-[#FF0000] font-mono text-xs tracking-widest uppercase font-bold" data-id="element-10026">
                System Configuration
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-black text-white mb-2 uppercase tracking-tight" data-id="element-10027">
              Settings & Preferences
            </h1>
            <p className="text-gray-400 font-mono text-sm max-w-xl" data-id="element-10028">
              Manage your account parameters, security protocols, and
              notification preferences.
            </p>
          </div>

          {/* Success/Error Messages */}
          {success && <div className="bg-green-500/10 border border-green-500/30 p-4 mb-8 flex items-center gap-3" data-id="element-10029">
              <Check className="text-green-500 flex-shrink-0" size={20} data-id="element-10030" />
              <p className="text-green-500 text-sm font-mono uppercase tracking-wide" data-id="element-10031">
                {success}
              </p>
            </div>}
          {error && <div className="bg-[#FF0000]/10 border border-[#FF0000]/30 p-4 mb-8 flex items-center gap-3" data-id="element-10032">
              <AlertTriangle className="text-[#FF0000] flex-shrink-0" size={20} data-id="element-10033" />
              <p className="text-[#FF0000] text-sm font-mono uppercase tracking-wide" data-id="element-10034">
                {error}
              </p>
            </div>}

          {/* Subscription Card */}
          <div className="bg-[#0a0a0a] border border-gray-800 p-8 mb-8 relative group" data-id="element-10035">
            {/* Corner Brackets */}
            <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10036" />
            <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10037" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10038" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10039" />

            <div className="flex items-start gap-4 mb-6" data-id="element-10040">
              <div className={`flex-shrink-0 w-12 h-12 flex items-center justify-center border ${userTier === 'pro' ? 'bg-[#FFD700]/10 border-[#FFD700] text-[#FFD700]' : userTier === 'active' ? 'bg-[#FF0000]/10 border-[#FF0000] text-[#FF0000]' : 'bg-gray-900 border-gray-700 text-gray-500'}`} data-id="element-10041">
                {userTier === 'pro' || userTier === 'active' ? <Crown size={24} data-id="element-10042" /> : <Sparkles size={24} data-id="element-10043" />}
              </div>
              <div className="flex-1 min-w-0" data-id="element-10044">
                <h3 className="font-bold text-white text-lg uppercase tracking-wide mb-1" data-id="element-10045">
                  {loadingProfile ? 'Loading...' : userTier === 'pro' ? 'Pro Plan Active' : userTier === 'active' ? 'Pay Per Scan' : 'Free Plan'}
                </h3>
                <p className="text-sm text-gray-400 font-mono" data-id="element-10046">
                  {loadingProfile ? 'Verifying clearance...' : userTier === 'pro' ? 'Unlimited access enabled' : userTier === 'active' ? `${scansCount} scan${scansCount !== 1 ? 's' : ''} remaining` : 'Limited features available'}
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-10047">
              <button onClick={() => setShowSubscriptionModal(true)} className="flex-1 bg-[#FF0000] text-white py-3 font-bold hover:bg-[#FF0000]/90 transition-all text-xs uppercase tracking-widest shadow-[0_0_15px_rgba(255,0,0,0.3)]" data-id="element-10048">
                {userTier === 'pro' ? 'View Plan Details' : 'Upgrade Plan'}
              </button>
              {(userTier === 'pro' || userTier === 'active') && <button onClick={() => setShowBillingModal(true)} className="flex-1 bg-transparent border border-gray-700 text-gray-300 py-3 font-bold hover:border-gray-500 hover:text-white transition-all text-xs uppercase tracking-widest" data-id="element-10049">
                  Billing History
                </button>}
            </div>
          </div>

          {/* Account Information */}
          <div className="bg-[#0a0a0a] border border-gray-800 p-8 mb-8 relative group" data-id="element-10050">
            {/* Corner Brackets */}
            <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10051" />
            <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10052" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10053" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10054" />

            <div className="flex items-center gap-3 mb-6" data-id="element-10055">
              <User size={20} className="text-[#FF0000]" data-id="element-10056" />
              <h3 className="font-bold text-white uppercase tracking-wider" data-id="element-10057">
                Identity Parameters
              </h3>
            </div>

            <form onSubmit={handleSaveChanges} className="space-y-6" data-id="element-10058">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-10059">
                <div data-id="element-10060">
                  <label className="block text-xs font-bold text-gray-500 mb-2 uppercase tracking-widest font-mono" data-id="element-10061">
                    First Name
                  </label>
                  <input type="text" value={formData.firstName} onChange={e => setFormData({
                  ...formData,
                  firstName: e.target.value
                })} className="w-full px-4 py-3 bg-[#050505] border border-gray-800 focus:outline-none focus:border-[#FF0000] focus:ring-1 focus:ring-[#FF0000] text-white transition-all font-mono text-sm" data-id="element-10062" />
                </div>
                <div data-id="element-10063">
                  <label className="block text-xs font-bold text-gray-500 mb-2 uppercase tracking-widest font-mono" data-id="element-10064">
                    Last Name
                  </label>
                  <input type="text" value={formData.lastName} onChange={e => setFormData({
                  ...formData,
                  lastName: e.target.value
                })} className="w-full px-4 py-3 bg-[#050505] border border-gray-800 focus:outline-none focus:border-[#FF0000] focus:ring-1 focus:ring-[#FF0000] text-white transition-all font-mono text-sm" data-id="element-10065" />
                </div>
              </div>

              <div data-id="element-10066">
                <label className="block text-xs font-bold text-gray-500 mb-2 uppercase tracking-widest font-mono" data-id="element-10067">
                  Email Address
                </label>
                <div className="flex gap-3" data-id="element-10068">
                  <div className="flex-1 px-4 py-3 bg-[#050505] border border-gray-800 text-gray-400 font-mono text-sm flex items-center" data-id="element-10069">
                    <Mail size={14} className="mr-3 text-gray-600" data-id="element-10070" />
                    {user?.email || ''}
                  </div>
                  <button type="button" onClick={() => setShowChangeEmailModal(true)} className="px-6 py-3 bg-gray-900 border border-gray-700 text-white font-bold hover:bg-gray-800 hover:border-gray-500 transition-all text-xs uppercase tracking-widest whitespace-nowrap" data-id="element-10071">
                    Change
                  </button>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-800" data-id="element-10072">
                <button type="submit" disabled={loading} className="w-full bg-white text-black py-3 font-bold hover:bg-gray-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-xs uppercase tracking-widest flex items-center justify-center gap-2" data-id="element-10073">
                  {loading ? 'Processing Update...' : <>
                      <Save size={16} data-id="element-10074" />
                      Save Changes
                    </>}
                </button>
              </div>
            </form>
          </div>

          {/* Notifications */}
          <div className="bg-[#0a0a0a] border border-gray-800 p-8 mb-8 relative group" data-id="element-10075">
            {/* Corner Brackets */}
            <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10076" />
            <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10077" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10078" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10079" />

            <div className="flex items-center gap-3 mb-6" data-id="element-10080">
              <Bell size={20} className="text-[#FF0000]" data-id="element-10081" />
              <h3 className="font-bold text-white uppercase tracking-wider" data-id="element-10082">
                Alert Protocols
              </h3>
            </div>

            <div className="space-y-6" data-id="element-10083">
              <div className="flex items-center justify-between" data-id="element-10084">
                <div data-id="element-10085">
                  <p className="font-bold text-white text-sm uppercase tracking-wide" data-id="element-10086">
                    Email Reports
                  </p>
                  <p className="text-xs text-gray-500 font-mono mt-1" data-id="element-10087">
                    Receive detailed analysis via secure email
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-id="element-10088">
                  <input type="checkbox" checked={emailNotifications} onChange={e => handleNotificationChange('email', e.target.checked)} className="sr-only peer" data-id="element-10089" />
                  <div className="w-11 h-6 bg-gray-900 border border-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-gray-500 after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF0000]/20 peer-checked:border-[#FF0000] peer-checked:after:bg-[#FF0000] peer-checked:after:border-[#FF0000]" data-id="element-10090"></div>
                </label>
              </div>

              <div className="h-px bg-gray-900" data-id="element-10091" />

              <div className="flex items-center justify-between" data-id="element-10092">
                <div data-id="element-10093">
                  <p className="font-bold text-white text-sm uppercase tracking-wide" data-id="element-10094">
                    System Alerts
                  </p>
                  <p className="text-xs text-gray-500 font-mono mt-1" data-id="element-10095">
                    Real-time notifications for completed scans
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-id="element-10096">
                  <input type="checkbox" checked={pushNotifications} onChange={e => handleNotificationChange('push', e.target.checked)} className="sr-only peer" data-id="element-10097" />
                  <div className="w-11 h-6 bg-gray-900 border border-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-gray-500 after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF0000]/20 peer-checked:border-[#FF0000] peer-checked:after:bg-[#FF0000] peer-checked:after:border-[#FF0000]" data-id="element-10098"></div>
                </label>
              </div>
            </div>
          </div>

          {/* Security */}
          <div className="bg-[#0a0a0a] border border-gray-800 p-8 mb-8 relative group" data-id="element-10099">
            {/* Corner Brackets */}
            <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10100" />
            <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10101" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10102" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-10103" />

            <div className="flex items-center gap-3 mb-6" data-id="element-10104">
              <Shield size={20} className="text-[#FF0000]" data-id="element-10105" />
              <h3 className="font-bold text-white uppercase tracking-wider" data-id="element-10106">
                Security Protocols
              </h3>
            </div>

            <div className="space-y-4" data-id="element-10107">
              <button onClick={() => setShowTwoFactorModal(true)} className="w-full flex items-center justify-between p-4 bg-[#050505] border border-gray-800 hover:border-gray-600 transition-all group/btn" data-id="element-10108">
                <div className="flex items-center gap-4" data-id="element-10109">
                  <div className="p-2 bg-gray-900 border border-gray-800 group-hover/btn:border-gray-600 transition-colors" data-id="element-10110">
                    <Lock size={16} className="text-gray-400" data-id="element-10111" />
                  </div>
                  <span className="font-bold text-gray-300 text-sm uppercase tracking-wide group-hover/btn:text-white" data-id="element-10112">
                    Two-Factor Authentication
                  </span>
                </div>
                <ChevronRight size={16} className="text-gray-600 group-hover/btn:text-[#FF0000] transition-colors" data-id="element-10113" />
              </button>

              <button onClick={() => setShowPrivacyModal(true)} className="w-full flex items-center justify-between p-4 bg-[#050505] border border-gray-800 hover:border-gray-600 transition-all group/btn" data-id="element-10114">
                <div className="flex items-center gap-4" data-id="element-10115">
                  <div className="p-2 bg-gray-900 border border-gray-800 group-hover/btn:border-gray-600 transition-colors" data-id="element-10116">
                    <Shield size={16} className="text-gray-400" data-id="element-10117" />
                  </div>
                  <span className="font-bold text-gray-300 text-sm uppercase tracking-wide group-hover/btn:text-white" data-id="element-10118">
                    Privacy Configuration
                  </span>
                </div>
                <ChevronRight size={16} className="text-gray-600 group-hover/btn:text-[#FF0000] transition-colors" data-id="element-10119" />
              </button>
            </div>
          </div>

          {/* Danger Zone */}
          <div className="bg-[#FF0000]/5 border border-[#FF0000]/30 p-8 mb-8 relative" data-id="element-10120">
            <h3 className="font-bold text-[#FF0000] mb-4 flex items-center gap-2 uppercase tracking-wider text-sm" data-id="element-10121">
              <AlertTriangle size={16} data-id="element-10122" />
              Critical Zone
            </h3>
            <p className="text-xs text-gray-400 mb-6 font-mono" data-id="element-10123">
              WARNING: Account deletion is irreversible. All forensic data and
              analysis logs will be permanently purged.
            </p>
            <button onClick={() => setShowDeleteAccountModal(true)} className="w-full bg-transparent border border-[#FF0000] text-[#FF0000] py-3 font-bold hover:bg-[#FF0000] hover:text-white transition-all text-xs uppercase tracking-widest flex items-center justify-center gap-2" data-id="element-10124">
              <Trash2 size={16} data-id="element-10125" />
              Delete Account
            </button>
          </div>

          {/* Logout Button */}
          <button onClick={handleLogout} className="w-full bg-gray-900 border border-gray-800 text-gray-400 py-4 font-bold hover:bg-gray-800 hover:text-white hover:border-gray-600 transition-all text-xs uppercase tracking-widest" data-id="element-10126">
            Terminate Session
          </button>
        </main>
      </div>

      {/* Modals */}
      <ChangeEmailModal isOpen={showChangeEmailModal} onClose={() => setShowChangeEmailModal(false)} data-id="element-10127" />
      <TwoFactorModal isOpen={showTwoFactorModal} onClose={() => setShowTwoFactorModal(false)} data-id="element-10128" />
      <PrivacySettingsModal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} data-id="element-10129" />
      <SubscriptionModal isOpen={showSubscriptionModal} onClose={() => setShowSubscriptionModal(false)} currentPlan={currentPlan} data-id="element-10130" />
      <DeleteAccountModal isOpen={showDeleteAccountModal} onClose={() => setShowDeleteAccountModal(false)} onConfirm={handleDeleteAccount} loading={deleteLoading} data-id="element-10131" />
      <BillingManagementModal isOpen={showBillingModal} onClose={() => setShowBillingModal(false)} currentPlan={currentPlan as 'pay-per-scan' | 'pro'} data-id="element-10132" />
      <CancelSubscriptionModal isOpen={showCancelModal} onClose={() => setShowCancelModal(false)} currentPlan={currentPlan as 'pay-per-scan' | 'pro'} data-id="element-10133" />
    </div>;
}